/**
 * Werving — /werving: vacatures-overzicht (Homerun "Jobs").
 *
 * Per vacature één rustige strook met het aantal sollicitaties per fase, rechts wat jij nog moet beoordelen.
 * Filter Concept / Open / Gesloten. Bovenaan "Te koppelen (n)" als de AI een sollicitatie niet zeker aan een
 * vacature kon koppelen (alleen met recht Werving bewerken). Zichtbaarheid zit in de API: zonder bewerkrecht
 * zie je alleen de vacatures waar je beoordelaar bent.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, InputAdornment, MenuItem, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { wervingApi } from './wervingApi';
import VacatureDialoog from './VacatureDialoog';
import TeamsBeheer from './TeamsBeheer';
import StagetypesBeheer from './StagetypesBeheer';
import {
  ACCENT, EIND_FASEN, INKT, KOLOM_BG, LIJN, PROBLEEM, STIL, dagenGeleden, faseKleur, filterVacatures, stageRegels,
} from './wervingLogica';

const WACHT_TE_LANG = 7; // dagen: daarna is wachten echt te lang (rood)

function Strook({ v, fasen, onFase }) {
  return (
    <Box sx={{ display: 'flex', borderTop: `1px solid ${LIJN}`, overflowX: 'auto' }}>
      <Box sx={{ flex: '1 0 96px', py: 1.5, textAlign: 'center', borderRight: `1px solid ${LIJN}` }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: INKT }}>{v.in_proces}</Typography>
        <Typography sx={{ fontSize: 12, color: STIL }}>In proces</Typography>
      </Box>
      {fasen.map((f, i) => {
        const n = v.aantallen[f.sleutel] || 0;
        const eersteEind = f.sleutel === EIND_FASEN[0];
        return (
          <Box key={f.sleutel} role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onFase(f.sleutel); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onFase(f.sleutel); } }}
            sx={{ flex: '1 0 96px', py: 1.5, textAlign: 'center', cursor: 'pointer', borderLeft: eersteEind && i ? `1px solid ${LIJN}` : 'none',
              '&:hover': { bgcolor: KOLOM_BG } }}>
            <Typography sx={{ fontSize: 18, fontWeight: n ? 700 : 400, color: n ? faseKleur(f.sleutel).tekst : '#b6bcc4' }}>{n}</Typography>
            <Typography sx={{ fontSize: 12, color: n ? STIL : '#b6bcc4', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: n ? faseKleur(f.sleutel).stip : '#dfe3e8' }} />{f.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Onderverdeling van de open ingang: lopende sollicitaties per stagetype (klik = pipeline gefilterd). */
function StageStrook({ v, onKies }) {
  const regels = stageRegels(v.stage_indeling);
  if (!regels.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, px: 2.5, py: 1.25, borderTop: `1px solid ${LIJN}`, bgcolor: KOLOM_BG }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: STIL, mr: 0.5 }}>Stages</Typography>
      {regels.map((r) => (
        <Box key={r.sleutel} role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onKies(r.sleutel); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onKies(r.sleutel); } }}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 1.1, py: 0.35, borderRadius: 10, cursor: 'pointer',
            border: `1px solid ${LIJN}`, bgcolor: '#fff', fontSize: 13, color: r.n ? INKT : '#9aa3ad', '&:hover': { borderColor: '#cfd6e0' } }}>
          {r.label}<Box component="span" sx={{ fontWeight: 700, color: r.n ? INKT : '#b6bcc4' }}>{r.n}</Box>
        </Box>
      ))}
    </Box>
  );
}

function TeKoppelenDialoog({ open, vacatures, onClose, onGewijzigd }) {
  const navigate = useNavigate();
  const [lijst, setLijst] = useState(null);
  const [keuze, setKeuze] = useState({});
  const laad = useCallback(() => wervingApi.teKoppelen().then((d) => setLijst(d.sollicitaties || [])).catch((e) => toast.error(e.message)), []);
  useEffect(() => { if (open) { setLijst(null); laad(); } }, [open, laad]);

  const koppel = async (s) => {
    const vid = keuze[s.id] ?? s.voorstel?.vacature_id;
    if (!vid) { toast.error('Kies eerst een vacature'); return; }
    try {
      await wervingApi.koppel(s.id, vid);
      setLijst((l) => l.filter((x) => x.id !== s.id));
      onGewijzigd();
      biedOngedaanAan(`${s.naam} gekoppeld`, async () => { await wervingApi.koppel(s.id, null); onGewijzigd(); laad(); });
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>Te koppelen</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: STIL, mb: 2 }}>
          Sollicitaties die de AI niet zeker (≥ 90%) aan een vacature kon koppelen. Kies de vacature; een voorstel staat al klaar.
        </Typography>
        {lijst === null ? <CircularProgress size={18} /> : lijst.length === 0 ? (
          <Typography sx={{ color: STIL }}>Niets meer te koppelen.</Typography>
        ) : lijst.map((s) => (
          <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderTop: `1px solid ${LIJN}`, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
              <Typography role="button" onClick={() => navigate(`/werving/sollicitatie/${s.id}`)}
                sx={{ fontWeight: 600, color: INKT, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>{s.naam}</Typography>
              <Typography sx={{ fontSize: 12, color: STIL }}>{dagenGeleden(s.ontvangen_op)}</Typography>
            </Box>
            <TextField select size="small" sx={{ minWidth: 200 }} value={keuze[s.id] ?? s.voorstel?.vacature_id ?? ''}
              onChange={(e) => setKeuze((k) => ({ ...k, [s.id]: e.target.value }))} label={s.voorstel ? 'Voorstel AI' : 'Vacature'}>
              {vacatures.filter((v) => v.status !== 'gesloten').map((v) => <MenuItem key={v.id} value={v.id}>{v.titel}</MenuItem>)}
            </TextField>
            <Button size="small" variant="outlined" onClick={() => koppel(s)} sx={{ textTransform: 'none', color: ACCENT, borderColor: LIJN }}>Koppelen</Button>
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export default function Vacatures() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [koppelOpen, setKoppelOpen] = useState(false);
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);

  const laad = useCallback(() => wervingApi.overzicht().then((d) => { setData(d); setFout(null); }).catch((e) => setFout(e.message)), []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  const zichtbaar = useMemo(() => filterVacatures(data?.vacatures, status, q), [data, status, q]);
  const telling = useMemo(() => Object.fromEntries(['concept', 'open', 'gesloten'].map((s) => [s, (data?.vacatures || []).filter((v) => v.status === s).length])), [data]);
  const teDoen = (data?.vacatures || []).reduce((a, v) => a + (v.te_beoordelen_door_jou || 0), 0);

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="person_search" title="Werving"
        subtitle={teDoen ? `Je hebt ${teDoen} sollicitatie${teDoen === 1 ? '' : 's'} te beoordelen` : 'Vacatures en sollicitaties uit ikwilwerken@'}
        rightActions={data?.kan_bewerken && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="text" size="small" startIcon={<GroupsOutlinedIcon />} onClick={() => setTeamsOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, color: STIL }}>Teams & teamleads</Button>
            <Button variant="text" size="small" startIcon={<SchoolOutlinedIcon />} onClick={() => setStagesOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, color: STIL }}>Stagetypes</Button>
            <Button variant="contained" disableElevation size="small" startIcon={<AddIcon />} onClick={() => setNieuwOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>Nieuwe vacature</Button>
          </Box>
        )} />

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75, py: 0.5, borderColor: LIJN, color: STIL },
            '& .Mui-selected': { color: `${INKT} !important`, bgcolor: '#fff !important', fontWeight: 600 } }}>
          <ToggleButton value="concept">Concept{telling.concept ? ` · ${telling.concept}` : ''}</ToggleButton>
          <ToggleButton value="open">Open{telling.open ? ` · ${telling.open}` : ''}</ToggleButton>
          <ToggleButton value="gesloten">Gesloten{telling.gesloten ? ` · ${telling.gesloten}` : ''}</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" placeholder="Zoeken" value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: STIL }} /></InputAdornment> }} />
      </Box>

      {data?.kan_bewerken && data.te_koppelen > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2.5, px: 2.5, py: 1.5,
          border: `1px solid ${LIJN}`, borderRadius: 3, bgcolor: KOLOM_BG }}>
          <Typography sx={{ fontSize: 14, color: INKT, flex: 1 }}>
            <b>{data.te_koppelen}</b> sollicitatie{data.te_koppelen === 1 ? '' : 's'} nog aan een vacature koppelen
            <Box component="span" sx={{ color: STIL }}> — de AI was niet zeker genoeg</Box>
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setKoppelOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, color: ACCENT, borderColor: '#cfd6e0' }}>Koppelen</Button>
        </Box>
      )}

      {fout ? <Typography sx={{ color: STIL }}>Kon de vacatures niet laden: {fout}</Typography>
        : !data ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={24} /></Box>
          : zichtbaar.length === 0 ? (
            <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: INKT }}>
                {data.vacatures.length ? `Geen vacatures met status ${status}` : data.kan_bewerken ? 'Nog geen vacatures; voeg er een toe' : 'Je bent nog geen beoordelaar van een vacature'}
              </Typography>
              <Typography sx={{ mt: 1, color: STIL, fontSize: 14 }}>
                {data.kan_bewerken ? 'Een vacature heeft 3–4 criteria voor de scorecard. Sollicitaties uit ikwilwerken@ komen er vanzelf in.'
                  : 'Joanne of het MT voegt je toe als beoordelaar van een vacature.'}
              </Typography>
            </Box>
          ) : zichtbaar.map((v) => (
            <Box key={v.id} onClick={() => navigate(`/werving/vacature/${v.id}`)}
              sx={{ bgcolor: '#fff', border: `1px solid ${LIJN}`, borderRadius: 3, mb: 2, cursor: 'pointer', overflow: 'hidden',
                '&:hover': { borderColor: '#d5dae1' } }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, px: 2.5, py: 1.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: INKT }}>{v.titel}</Typography>
                <Typography sx={{ fontSize: 13, color: STIL }}>
                  {[(data.disciplines || []).find((d) => d.sleutel === v.discipline)?.label, v.demo && 'demo'].filter(Boolean).join(' · ')}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {v.ben_beoordelaar && (v.te_beoordelen_door_jou ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {v.oudste_wacht_dagen != null && (
                      <Typography sx={{ fontSize: 12.5, color: v.oudste_wacht_dagen >= WACHT_TE_LANG ? PROBLEEM : STIL }}>
                        oudste wacht {v.oudste_wacht_dagen === 0 ? 'sinds vandaag' : `${v.oudste_wacht_dagen} dag${v.oudste_wacht_dagen === 1 ? '' : 'en'}`}
                      </Typography>
                    )}
                    <Button size="small" variant="contained" disableElevation
                      onClick={(e) => { e.stopPropagation(); navigate(`/werving/vacature/${v.id}?mijn=1`); }}
                      sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
                      Beoordelen ({v.te_beoordelen_door_jou})
                    </Button>
                  </Box>
                ) : <Typography sx={{ fontSize: 13, color: STIL }}>Alles beoordeeld</Typography>)}
              </Box>
              <Strook v={v} fasen={data.fasen} onFase={(f) => navigate(`/werving/vacature/${v.id}?fase=${f}`)} />
              {v.soort === 'open' && <StageStrook v={v} onKies={(st) => navigate(`/werving/vacature/${v.id}?stagetype=${encodeURIComponent(st)}`)} />}
            </Box>
          ))}

      <VacatureDialoog open={nieuwOpen} disciplines={data?.disciplines} onClose={() => setNieuwOpen(false)}
        onOpgeslagen={(v) => { setNieuwOpen(false); navigate(`/werving/vacature/${v.id}`); }} />
      {data?.kan_bewerken && <TeamsBeheer open={teamsOpen} onClose={() => setTeamsOpen(false)} onGewijzigd={laad} />}
      {data?.kan_bewerken && <StagetypesBeheer open={stagesOpen} onClose={() => setStagesOpen(false)} onGewijzigd={laad} />}
      <TeKoppelenDialoog open={koppelOpen} vacatures={data?.vacatures || []} onClose={() => setKoppelOpen(false)} onGewijzigd={laad} />
    </Box>
  );
}
